import { supabase } from "../../lib/supabaseClient";
import { mapProfile } from "./mappers";

function requireClient() {
  if (!supabase) throw new Error("Supabase is not configured.");
  return supabase;
}

function getAuthGoogleUrl() {
  const base = process.env.REACT_APP_SUPABASE_URL?.replace(/\/$/, "") || "";
  if (!base) return "";
  return `${base}/functions/v1/auth-google`;
}

/**
 * Exchange a Google OAuth access token for a Supabase session whose JWT
 * carries app_metadata.google_sub (used by RLS).
 */
export async function establishSupabaseSessionFromGoogle(accessToken) {
  const client = requireClient();
  const url = getAuthGoogleUrl();
  const anonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || "";
  if (!url || !anonKey) {
    throw new Error("Auth is not configured.");
  }

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${anonKey}`,
      apikey: anonKey,
    },
    body: JSON.stringify({ accessToken }),
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload.error || "Could not establish secure session.");
  }

  const { error: verifyError } = await client.auth.verifyOtp({
    token_hash: payload.tokenHash,
    type: "email",
  });
  if (verifyError) {
    throw new Error(verifyError.message || "Could not verify session.");
  }

  return {
    googleSub: payload.googleSub,
    profile: payload.profile ? mapProfile(payload.profile) : null,
  };
}

export async function getSupabaseSession() {
  const { data, error } = await requireClient().auth.getSession();
  if (error) throw error;
  return data.session;
}

export async function getGoogleSubFromSession(session) {
  const s = session ?? (await getSupabaseSession());
  if (!s?.user) return null;
  return (
    s.user.app_metadata?.google_sub ||
    s.user.user_metadata?.google_sub ||
    null
  );
}

export async function signOutSupabase() {
  await requireClient().auth.signOut();
}
